import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, X, Pencil } from "lucide-react";

/**
 * Reusable CRUD page for simple master-data resources.
 *
 * Props:
 *  - title, description, eyebrow, testIdPrefix
 *  - resource: REST collection path, e.g. "origins"
 *  - columns: [{ key, label, render?, align?, mono? }]
 *  - fields: [{ key, label, type?, placeholder?, required?, options?, default? }]
 *  - canWrite: bool        — show "New" + delete buttons
 *  - canEdit: bool         — show pencil icon → edit modal (uses PATCH)
 *  - readOnly: bool        — force read-only (overrides canWrite/canEdit)
 *  - emptyHint: string
 *  - readOnlyNotice?: string — shown in a soft banner at top of read-only pages
 */
export default function CrudPage({
  title, description, eyebrow, testIdPrefix,
  resource, columns, fields, canWrite, canEdit, readOnly, emptyHint, readOnlyNotice,
}) {
  const allowAdd  = canWrite && !readOnly;
  const allowDel  = canWrite && !readOnly;
  const allowEdit = (canEdit ?? canWrite) && !readOnly;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);          // create modal
  const [editing, setEditing] = useState(null);     // record being edited
  const blankForm = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "boolean" ? false : "")]));
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${resource}`);
      setItems(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [resource]);

  const startEdit = (it) => {
    const f = {};
    fields.forEach((field) => { f[field.key] = it[field.key] ?? (field.default ?? (field.type === "boolean" ? false : "")); });
    setForm(f);
    setEditing(it);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.key] = parseFloat(payload[f.key]) || 0;
      if (f.type === "boolean") payload[f.key] = !!payload[f.key];
    });
    try {
      if (editing) {
        await api.patch(`/${resource}/${editing.id}`, payload);
      } else {
        await api.post(`/${resource}`, payload);
      }
      setOpen(false); setEditing(null);
      setForm(blankForm());
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await api.delete(`/${resource}/${id}`);
      await load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const showActionsCol = allowDel || allowEdit;
  const modalOpen = open || !!editing;

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={allowAdd && (
          <button
            data-testid={`${testIdPrefix}-add-button`}
            onClick={() => { setForm(blankForm()); setOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]"
          >
            <Plus className="w-4 h-4" /> New
          </button>
        )}
      />

      {readOnlyNotice && (
        <div className="bg-secondary/40 border border-border rounded p-3 text-xs mb-4" data-testid={`${testIdPrefix}-readonly-notice`}>
          {readOnlyNotice}
        </div>
      )}

      {error && !modalOpen && <p className="text-destructive text-sm mb-4">{error}</p>}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
          <thead>
            <tr className="bg-secondary">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`label-tiny px-4 py-3 text-left ${c.align === "right" ? "text-right" : ""}`}
                >
                  {c.label}
                </th>
              ))}
              {showActionsCol && <th className="w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + (showActionsCol ? 1 : 0)} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={columns.length + (showActionsCol ? 1 : 0)} className="px-4 py-12 text-center text-muted-foreground">
                  <p className="mb-1">No entries yet.</p>
                  <p className="text-xs">{emptyHint}</p>
                </td>
              </tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.id} className={`border-t border-border hover:bg-secondary/40 ${it.is_active === false ? "opacity-60" : ""}`}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-2.5 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono text-xs" : ""}`}
                  >
                    {c.render ? c.render(it) : (it[c.key] ?? "")}
                  </td>
                ))}
                {showActionsCol && (
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {allowEdit && (
                      <button
                        onClick={() => startEdit(it)}
                        data-testid={`${testIdPrefix}-edit-${it.id}`}
                        className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {allowDel && (
                      <button
                        onClick={() => onDelete(it.id)}
                        data-testid={`${testIdPrefix}-delete-${it.id}`}
                        className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid={`${testIdPrefix}-modal`}>
          <div className="bg-card border border-border rounded shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-display text-lg font-bold">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              <button onClick={() => { setOpen(false); setEditing(null); }} className="text-muted-foreground hover:text-foreground" data-testid={`${testIdPrefix}-modal-close`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-5 space-y-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="label-tiny mb-1.5 block">{f.label}{f.required && " *"}</label>
                  {f.type === "select" ? (
                    <select
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={f.required}
                      data-testid={`${testIdPrefix}-input-${f.key}`}
                      className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">Select…</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : f.type === "boolean" ? (
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                        data-testid={`${testIdPrefix}-input-${f.key}`}
                      />
                      <span className="text-sm">{f.placeholder || "Enabled"}</span>
                    </label>
                  ) : f.type === "textarea" ? (
                    <textarea
                      rows={3}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      required={f.required}
                      data-testid={`${testIdPrefix}-input-${f.key}`}
                      className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      step={f.type === "number" ? "any" : undefined}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      required={f.required}
                      data-testid={`${testIdPrefix}-input-${f.key}`}
                      className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  )}
                </div>
              ))}
              {error && <p className="text-destructive text-sm">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={`${testIdPrefix}-modal-submit`}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60"
                >
                  {submitting ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
